import React, { useEffect, useState, useMemo } from 'react';  
import { Select, Table, Typography, Card, Flex, Row, Col, Statistic, DatePicker } from 'antd';  
import { useSettings } from '../../contexts/settingsContext';  
import { loadState } from "../../stores/store";  
import { getAllInvoicesWithContestants } from '../../api/invoice';  
import { fetchApplicationsTimeline, fetchStats } from '../../api/application';  
import { fetchAllCompetitions } from '../../api/competition';  
import { searchContestants } from '../../api/contestant';  
import { getTotalGroups } from '../../api/group';
import './stats.scss';  
import { Area, AreaChart, Bar, BarChart, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';  
import dayjs from 'dayjs';  
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';  
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'; 

dayjs.extend(isSameOrAfter);  
dayjs.extend(isSameOrBefore);
  
const { RangePicker } = DatePicker;  
const { Option } = Select;  
const { Title, Text } = Typography;  
  
const Stats = () => {  
    const { state: settings } = useSettings();  
    const [stats, setStats] = useState([]);  
    const [applicationTimeline, setApplicationTimeline] = useState([]);  
    const [applicationTimelineStartDate, setApplicationTimelineStartDate] = useState(null);  
    const [applicationTimelineEndDate, setApplicationTimelineEndDate] = useState(null);  
    const [invoices, setInvoices] = useState([]);  
    const [loading, setLoading] = useState(false);  
    const [currentCompetition, setCurrentCompetition] = useState(null);  
    const [contestants, setContestants] = useState([]);  
    const [selectedContestant, setSelectedContestant] = useState(null);  
    const [totalGroups, setTotalGroups] = useState(null);
    const state = loadState();  
  
    useEffect(() => {  
        const loadStats = async () => {  
            setLoading(true);  
      
            try {  
                const [  
                    applicationStats,  
                    fetchedApplicationTimeline,  
                    fetchedInvoices,  
                    competitionDataArray,  
                    fetchedContestants,  
                    fetchedGroups
                ] = await Promise.all([  
                    fetchStats().catch((error) => {  
                        console.error('Error fetching application stats:', error);  
                        return null;  
                    }),  
                    fetchApplicationsTimeline().catch((error) => {  
                        console.error('Error fetching application timeline:', error);  
                        return [];  
                    }),  
                    getAllInvoicesWithContestants().catch((error) => {  
                        console.error('Error fetching invoices:', error);  
                        return [];  
                    }),  
                    fetchAllCompetitions().catch((error) => {  
                        console.error('Error fetching competitions:', error);  
                        return [];  
                    }),  
                    searchContestants().catch((error) => {  
                        console.error('Error fetching contestants:', error);  
                        return [];  
                    }),  
                    getTotalGroups().catch((error) => {  
                        console.error('Error fetching contestants:', error);  
                        return [];  
                    }), 
                ]);  
      
                // Select the current competition  
                const competition = competitionDataArray.find(  
                    (comp) => comp._id === settings.selectedCompetition._id  
                );  
                setCurrentCompetition(competition || null);  
                setStats(applicationStats || []);  
                setApplicationTimeline(fetchedApplicationTimeline.applicationCounts || []);  
                setTotalGroups(fetchedGroups.totalGroups);
      
                if (  
                    Array.isArray(fetchedApplicationTimeline.applicationCounts) &&  
                    fetchedApplicationTimeline.applicationCounts.length > 0  
                  ) {  
                    setApplicationTimelineStartDate(dayjs(fetchedApplicationTimeline.earliestDate));  
                    setApplicationTimelineEndDate(dayjs(fetchedApplicationTimeline.today));  
                  }    
                // Filter the invoices based on active province  
                const filteredInvoices = (fetchedInvoices || []).filter(  
                    (invoice) => invoice.province === state.user.activeProvince  
                );  
                setInvoices(filteredInvoices);  

                const filteredContestants = (fetchedContestants || []).filter(  
                    (contestant) => contestant.province === state.user.activeProvince  
                );
                setContestants(filteredContestants || []);  
      
                if (filteredContestants.length > 0) {  
                    const firstContestantId = filteredContestants[0]._id;  
                    setSelectedContestant(firstContestantId);  
                }  
                
            } catch (error) {  
                console.error('Error in loadStats function:', error);  
            } finally {  
                setLoading(false);  
            }  
        };  
        loadStats();  
    }, [settings.selectedCompetition]);  
  
    const getFilteredTimelineData = () => {  
        if (!applicationTimeline || !Array.isArray(applicationTimeline)) {  
          console.error('applicationTimeline is not an array:', applicationTimeline);  
          return [];  
        }  
        if (!applicationTimelineStartDate || !applicationTimelineEndDate) {  
          return applicationTimeline;  
        }  
      
        const filteredData = applicationTimeline.filter((item) => {  
          const date = dayjs(item.date);  
          return (  
            date.isSameOrAfter(applicationTimelineStartDate, 'day') &&  
            date.isSameOrBefore(applicationTimelineEndDate, 'day')  
          );  
        });  
      
        return filteredData;  
      };  
  
    const handleContestantChange = (value) => {  
        setSelectedContestant(value);  
    };  
  
    const handleDateRangeChange = (dates) => {  
        if (dates && dates.length === 2) {  
          setApplicationTimelineStartDate(dates[0]);  
          setApplicationTimelineEndDate(dates[1]);  
        } else {  
          setApplicationTimelineStartDate(null);  
          setApplicationTimelineEndDate(null);  
        }  
      };  
  
    const totalApplications = useMemo(  
        () => (stats || []).reduce((total, stat) => total + stat.count, 0),  
        [stats]  
    );  
    const totalSoloApplications = useMemo(  
        () => (stats || []).reduce((total, stat) => total + stat.soloApplicationsCount, 0),  
        [stats]  
    );  
    const totalGroupApplications = useMemo(  
        () => (stats || []).reduce((total, stat) => total + stat.groupApplicationsCount, 0),  
        [stats]  
    );  
  
    const invoiceStats = useMemo(() => {  
        const contestantInvoicesFiltered = invoices.filter((invoice) => invoice.contestant);  
        const allInvoices = [...contestantInvoicesFiltered];  
  
        return allInvoices.reduce((acc, invoice) => {  
            if (!invoice.paid) return acc;  
            const round = acc[invoice.roundNumber] || {  
                totalAmount: 0,  
                paidCount: 0,  
                soloPaidCount: 0,  
                soloTotalAmount: 0,  
            };  
            const isSolo = !!invoice.contestant;  
            round.totalAmount += invoice.fee.total_fee_paid;  
            round.paidCount += 1;  
            if (isSolo) {  
                round.soloPaidCount += 1;  
                round.soloTotalAmount += invoice.fee.total_fee_paid;  
            }  
            acc[invoice.roundNumber] = round;  
            return acc;  
        }, {});  
    }, [invoices]);  
  
    const totalMoney = useMemo(  
        () => Object.values(invoiceStats).reduce((total, stat) => total + stat.totalAmount, 0),  
        [invoiceStats]  
    );  
  
    const totalPaidInvoices = useMemo(  
        () => Object.values(invoiceStats).reduce((total, stat) => total + stat.paidCount, 0),  
        [invoiceStats]  
    );  
  
    const { totalAmountPaidByContestant, totalAmountNotPaidByContestant } = useMemo(() => {  
        const totalAmountPaid = {};  
        const totalAmountNotPaid = {};  
        invoices.forEach((invoice) => {  
            if (!invoice.contestant) return;  
            const contestantId = invoice.contestant._id;  
            totalAmountPaid[contestantId] = totalAmountPaid[contestantId] || 0;  
            totalAmountNotPaid[contestantId] = totalAmountNotPaid[contestantId] || 0;  
  
            if (invoice.paid) {  
                totalAmountPaid[contestantId] += invoice.fee.total_fee_paid;  
            } else {  
                totalAmountNotPaid[contestantId] += invoice.fee.total_fee_unpaid;  
            }  
        });  
        return {  
            totalAmountPaidByContestant: totalAmountPaid,  
            totalAmountNotPaidByContestant: totalAmountNotPaid,  
        };  
    }, [invoices]);  
  
    const contestantInvoices = useMemo(() => {  
        if (!selectedContestant) return [];  
        return invoices.filter(  
            (invoice) => invoice.contestant && invoice.contestant._id === selectedContestant  
        );  
    }, [invoices, selectedContestant]);  
  
    const totalPaidAmountContestant = useMemo(  
        () =>  
            contestantInvoices  
                .filter((invoice) => invoice.paid)  
                .reduce((total, invoice) => total + invoice.fee.total_fee_paid, 0),  
        [contestantInvoices]  
    );  
  
    const columns = [  
        {  
            title: 'Contestant',  
            dataIndex: 'name',  
            key: 'name',  
            render: (_, contestant) => `${contestant.first_name} ${contestant.last_name}`,  
        },  
        {  
            title: 'Total Amount Paid',  
            dataIndex: 'totalPaid',  
            key: 'totalPaid',  
            render: (_, contestant) => `R${totalAmountPaidByContestant[contestant._id] || 0}`,  
        },  
        {  
            title: 'Total Amount Not Paid',  
            dataIndex: 'totalNotPaid',  
            key: 'totalNotPaid',  
            render: (_, contestant) => `R${totalAmountNotPaidByContestant[contestant._id] || 0}`,  
        },  
    ];  
  
    return (
        <div>
            <Flex style={{ marginBottom: '15px' }} align="center" justify="space-between">
                <div>
                    <Title level={4} style={{ margin: 0 }}>
                        Statistics
                    </Title>
                    <Text type="secondary">View statistics for the current competition</Text>
                </div>
            </Flex>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card bordered={false} title="Metrics">
                        <Row gutter={[16, 16]}>
                            <Col span={24} md={6}>
                                <Statistic loading={loading} title="Total Applications" value={totalApplications} />
                            </Col>
                            <Col span={24} md={6}>
                                <Statistic loading={loading} title="Total Contestants" value={contestants.length} />
                            </Col>
                            <Col span={24} md={6}>
                                <Statistic
                                    loading={loading}
                                    title="Total Groups"
                                    value={totalGroups || 0}
                                />
                            </Col>
                            <Col span={24} md={6}>
                                <Statistic loading={loading} title="Total Sales" value={totalMoney} prefix="R" />
                            </Col>
                        </Row>
                    </Card>
                </Col>
                <Col span={24}>
                    <Card
                        loading={loading}
                        bordered={false}
                        title="Total Applications Over Time"
                        extra={
                            !loading && (
                                <RangePicker  
                                defaultValue={[applicationTimelineStartDate, applicationTimelineEndDate]}  
                                onChange={handleDateRangeChange}  
                                />  
                            )
                        }
                    >
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart
                                width={500}
                                height={200}
                                data={getFilteredTimelineData()}
                                margin={{
                                    top: 10,
                                    right: 30,
                                    left: 0,
                                    bottom: 0,
                                }}
                            >
                                <YAxis />
                                <XAxis dataKey="date" />
                                <Tooltip />
                                <Area
                                    type="monotone"
                                    dataKey="count"
                                    strokeWidth="2px"
                                    stroke="#1271dd"
                                    fill="#e6f6ff"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
                <Col span={24} md={12}>
                    <Card loading={loading} bordered={false} title="Financial Information">
                        <Row gutter={[16, 16]}>
                            <Col span={24} md={12}>
                                <Statistic title="Total Paid Invoices" value={totalPaidInvoices} />
                            </Col>
                            <Col span={24} md={12}>
                                <Statistic title="Total Sales" value={totalMoney} prefix="R" />
                            </Col>
                        </Row>
                    </Card>
                </Col>
                <Col span={24} md={12}>
                    <Card loading={loading} bordered={false} title="Application Information">
                        <Row gutter={[16, 16]}>
                            <Col span={24} md={8}>
                                <Statistic title="Solo Applications" value={totalSoloApplications} />
                            </Col>
                            <Col span={24} md={8}>
                                <Statistic title="Group Applications" value={totalGroupApplications} />
                            </Col>
                            <Col span={24} md={8}>
                                <Statistic title="Total Applications" value={totalApplications} />
                            </Col>
                        </Row>
                    </Card>
                </Col>
                <Col span={24}>
                    <Card title="List of Contestants" loading={loading}>
                        <Table
                            dataSource={contestants}
                            columns={columns}
                            rowKey="_id"
                            loading={loading}
                            scroll={{ x: true }}
                        />
                    </Card>
                </Col>
                <Col span={24}>
                    <Card
                        title="Invoices by Contestant"
                        extra={
                            <Select
                                showSearch
                                style={{ width: 200 }}
                                placeholder="Select a contestant"
                                onChange={(value) => handleContestantChange(value)}
                                optionFilterProp="children"
                                value={selectedContestant}
                            >
                                {contestants.map((contestant) => (
                                    <Option key={contestant._id} value={contestant._id}>
                                        {contestant.first_name} {contestant.last_name}
                                    </Option>
                                ))}
                            </Select>
                        }
                    >
                        {selectedContestant && (
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <Card type="inner" title="Metrics" size="small">
                                        <Row gutter={[16, 16]}>
                                            <Col span={24} md={8}>
                                                <Statistic title="Total Amount Paid" value={totalPaidAmountContestant} prefix="R" />
                                            </Col>
                                            <Col span={24} md={8}>
                                                <Statistic
                                                    title="Outstanding Invoices"
                                                    value={contestantInvoices.filter((invoice) => !invoice.paid).length}
                                                />
                                            </Col>
                                            <Col span={24} md={8}>
                                                <Statistic
                                                    title="Paid Invoices"
                                                    value={contestantInvoices.filter((invoice) => invoice.paid).length}
                                                />
                                            </Col>
                                        </Row>
                                    </Card>
                                </Col>
                                <Col span={24} md={12}>
                                    <Card type="inner" title="Applications" size="small">
                                        <ResponsiveContainer width="100%" height={400}>
                                            <BarChart data={contestantInvoices}>
                                                <XAxis dataKey="roundNumber" label="Round" />
                                                <YAxis />
                                                <Tooltip />
                                                <Legend />
                                                <Bar dataKey="applications.length" fill="#1271dd" name="Applications">
                                                    <LabelList dataKey="applications.length" fill="#FFFFFF" />
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Card>
                                </Col>
                                <Col span={24} md={12}>
                                    <Card type="inner" title="Invoices" size="small">
                                        <ResponsiveContainer width="100%" height={400}>
                                            <BarChart data={contestantInvoices}>
                                                <XAxis dataKey="roundNumber" label="Round" />
                                                <YAxis />
                                                <Tooltip />
                                                <Legend />
                                                <Bar dataKey="fee.total_fee_paid" fill="#1271dd" name="Invoice Amount">
                                                    <LabelList dataKey="fee.total_fee_paid" fill="#FFFFFF" />
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Card>
                                </Col>
                            </Row>
                        )}
                    </Card>
                </Col>
            </Row>
            <div className="stats-cards"></div>
        </div>
    );
};  
  
export default Stats;  